"use client";

import { useRef, useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";

import { PRICING } from "@/lib/pricing-data";
import { withRedirectUrl } from "@/lib/auth-redirect-url";
import { PLAN_MANAGEMENT_PATH } from "@/lib/billing/self-serve-checkout";
import { useDialogA11y } from "@/components/ui/use-dialog-a11y";
import {
  readBrowserConversionAttribution,
  readAdUserDataConsent,
  readAnalyticsConsent,
  readCookieValue,
} from "@/components/analytics/browser-attribution";

/**
 * Plan-aware checkout confirmation interstitial.
 *
 * Origin: Plan 09-03 / Phase 11 (Pro free-tier upsell). Consumed by
 * <UpgradeModal> (in-app, free-tier) and — as of the conversion-bundle work —
 * by the public pricing CTAs (<PricingCta>) as the confirmation step the brief
 * (Rec 2) asks for before the Stripe redirect.
 *
 * IMPORTANT — why this is now plan-aware: ticket PRICING-DIRECT-CHECKOUT-2026-05-22
 * removed this modal from the pricing page because it HARDCODED Pro/£39 copy, so
 * the Business CTA opened a Pro-framed summary (wrong price/terms) and erroring
 * on continue. The fix is not "no interstitial" — it is "show the right plan".
 * This component now resolves the plan (Pro monthly / Pro annual / Business)
 * from the priceId and renders the correct name, price, billing summary and
 * value list. Reusing it for any tier is now safe.
 *
 * The Continue handler is unchanged: POST /api/stripe/checkout, and on 401
 * (logged-out) bounce through Clerk signup → /checkout/resume/<priceId> so the
 * priceId survives the auth detour (it lives in the URL PATH because the Clerk
 * hosted portal strips query strings).
 */

type PlanKey = "monthly" | "annual" | "business";

interface CheckoutSummaryProps {
  open: boolean;
  priceId: string;
  onClose: () => void;
  /**
   * Optional plan override. Defaults to inferring the plan from the priceId
   * via PRICING from lib/pricing-data.ts.
   */
  planLabel?: PlanKey;
}

interface PlanSummary {
  key: PlanKey;
  /** Display heading, e.g. "Start Pro — £39/month". */
  heading: string;
  /** Exact-charge line shown in the highlighted box. */
  billedSummary: string;
  /** What the user unlocks. */
  valueItems: string[];
  /** Optional reassurance note (founder pricing, etc.). */
  note?: string;
}

const PRO_VALUE_ITEMS = [
  "Unlimited projects and documents",
  "Branded PDFs — your logo, no watermark",
  "COSHH assessments with one-click substance lookup",
  "Worker cert tracking with expiry alerts",
  "Site diary, permits to work and incident log",
  "Full audit-ready compliance pack",
];

const BUSINESS_VALUE_ITEMS = [
  "Everything in Pro",
  "Up to 10 internal team logins",
  "Up to 50 active site portal users",
  "Site files, drawings and procedures portal",
  "Site attendance and live check-in view",
  "Subcontractor evidence records and review",
];

const FOUNDER_NOTE =
  "Founding price — locked in while your subscription stays active.";

function resolvePlan(priceId: string, override?: PlanKey): PlanSummary {
  const key: PlanKey =
    override ??
    (priceId === PRICING.business.stripe.priceId
      ? "business"
      : priceId === PRICING.annual.stripe.priceId
        ? "annual"
        : "monthly");

  switch (key) {
    case "business":
      return {
        key,
        heading: `Start Business — ${PRICING.business.displayWithPeriod}`,
        billedSummary: `Billed ${PRICING.business.display} per month. Cancel anytime.`,
        valueItems: BUSINESS_VALUE_ITEMS,
      };
    case "annual":
      return {
        key,
        heading: `Start Pro — ${PRICING.annual.displayYearly}`,
        billedSummary: `Billed ${PRICING.annual.display}/mo as ${PRICING.annual.displayYearly} once a year. ${PRICING.savings.display} vs monthly.`,
        valueItems: PRO_VALUE_ITEMS,
        note: FOUNDER_NOTE,
      };
    case "monthly":
    default:
      return {
        key,
        heading: `Start Pro — ${PRICING.monthly.displayWithPeriod}`,
        billedSummary: `Billed ${PRICING.monthly.display} per month. Cancel anytime.`,
        valueItems: PRO_VALUE_ITEMS,
        note: FOUNDER_NOTE,
      };
  }
}

export default function CheckoutSummary({
  open,
  priceId,
  onClose,
  planLabel,
}: CheckoutSummaryProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Belt-and-suspenders against duplicate checkout-session creation: the
  // disabled attribute blocks most double-clicks, but a synchronous double
  // fire would slip through (state hasn't re-rendered yet), so guard with a ref.
  const inFlightRef = useRef(false);

  // Dialog semantics, Escape-to-close, focus trap and focus return — the shared
  // accessibility baseline (issue #1721).
  const dialogRef = useDialogA11y<HTMLDivElement>({ open, onClose });

  if (!open) return null;

  const plan = resolvePlan(priceId, planLabel);

  async function handleContinue() {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId,
          gaCookie:
            readAnalyticsConsent() === "accepted"
              ? readCookieValue("_ga")
              : null,
          attribution: readBrowserConversionAttribution(),
          adUserDataConsent: readAdUserDataConsent(),
        }),
      });
      if (res.status === 401) {
        // Not signed in — bounce through Clerk signup, then resume the
        // Checkout Session creation server-side at /checkout/resume/<priceId>
        // so the priceId survives the auth detour. priceId lives in the
        // URL PATH (not query string) because Clerk's signup handoff via
        // accounts.thesitebook.co.uk strips query parameters from
        // redirect_url, which made the original ?priceId=… variant land
        // back at /checkout/resume with no priceId → 307 → /pricing → free
        // account. Path segments are preserved through the Clerk hop.
        // /signup (no hyphen); /sign-up does not exist and 404s.
        const resume = `/checkout/resume/${encodeURIComponent(priceId)}`;
        window.location.href = withRedirectUrl("/signup", resume);
        return;
      }
      const data = (await res.json()) as { url?: string; error?: string };
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error === "plan_change_requires_billing_portal") {
        window.location.href = PLAN_MANAGEMENT_PATH;
      } else {
        setError("Something went wrong. Please try again.");
        setLoading(false);
        inFlightRef.current = false;
      }
    } catch {
      setError("Something went wrong. Please try again.");
      setLoading(false);
      inFlightRef.current = false;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={plan.heading}
        className="relative w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl focus:outline-none"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-text-tertiary hover:text-muted"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-xl font-bold text-foreground pr-6">
          {plan.heading}
        </h2>
        <p className="mt-1 text-sm text-muted">Here&apos;s what you unlock:</p>

        <ul className="mt-4 space-y-3">
          {plan.valueItems.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2.5 text-sm text-foreground"
            >
              <CheckCircle2 className="h-5 w-5 text-done shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-5 rounded-lg bg-attn-bg border border-attn-line px-4 py-3">
          <p className="text-sm font-semibold text-attn">
            {plan.billedSummary}
          </p>
          {plan.note && (
            <p className="text-xs text-brand-700 mt-0.5">{plan.note}</p>
          )}
        </div>

        {error && (
          <p className="mt-3 text-center text-sm text-danger">{error}</p>
        )}

        <button
          onClick={handleContinue}
          disabled={loading}
          className="mt-5 flex items-center justify-center gap-2 w-full rounded-xl bg-accent px-4 py-3.5 text-sm font-bold text-white transition-colors hover:bg-accent-dark disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Continue to payment →
        </button>

        <button
          onClick={onClose}
          className="mt-3 w-full text-center text-sm text-text-tertiary hover:text-muted"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
